import { ChordLib, type ModulationPlan } from '@tunasong/music-lib'
import { type FC } from 'react'
import styles from './modulation-phase-diagram.module.css'

export type ModulationPhase = 'source' | 'modulation' | 'destination'

export interface ModulationPhaseDiagramProps {
  plan: ModulationPlan
  currentPhase: ModulationPhase | null
}

const formatChords = (chords: ModulationPlan['sourceProgression']): string =>
  chords.map(c => ChordLib.getName(c)).join(' · ')

/**
 * Three-block phase diagram for the modulation audition (source → modulation
 * → destination). Highlights the block matching `currentPhase` while
 * playback advances. The center block shows the technique label and the
 * primary anchor chord/note (pivot chord name for pivot, common tone for
 * common-tone modulation; em-dash otherwise).
 *
 * Spec: docs/superpowers/specs/2026-05-26-modulation-lab-v1-design.md §5
 */
export const ModulationPhaseDiagram: FC<ModulationPhaseDiagramProps> = props => {
  const { plan, currentPhase } = props

  return (
    <div className={styles.diagram} role="group" aria-label="Modulation phase diagram">
      <div className={styles.block} data-active={currentPhase === 'source'}>
        <div className={styles.label}>Source</div>
        <div className={styles.keyName}>{plan.sourceKey}</div>
        <div className={styles.chords}>{formatChords(plan.sourceProgression)}</div>
      </div>
      <div className={styles.arrow}>→</div>
      <div className={styles.block} data-active={currentPhase === 'modulation'}>
        <div className={styles.label}>{plan.technique}</div>
        <div className={styles.keyName}>
          {plan.pivotChord ? ChordLib.getName(plan.pivotChord) : (plan.commonTone ?? '—')}
        </div>
        <div className={styles.chords}>{formatChords(plan.modulationProgression)}</div>
      </div>
      <div className={styles.arrow}>→</div>
      <div className={styles.block} data-active={currentPhase === 'destination'}>
        <div className={styles.label}>Destination</div>
        <div className={styles.keyName}>{plan.destinationKey}</div>
        <div className={styles.chords}>{formatChords(plan.destinationProgression)}</div>
      </div>
    </div>
  )
}
